import React from 'react'
import Dotdotdot from 'react-dotdotdot'
import { useSelector } from 'react-redux'
import { Grid } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import classNames from 'classnames'
import {
  AppointmentEventType,
  EventTypeName,
  LanguageUtils,
  SentenceFormatter,
  StateLabel,
  Text,
  Utils,
} from '@pbt/pbt-ui-components'

import { useAlertType } from '~/store/hooks/patient'
import { getAppointmentTypes, getEventState } from '~/store/reducers/constants'
import { getPatient } from '~/store/reducers/patients'
import { getSpaceName } from '~/store/reducers/spaces'
import { getTimetableEvent } from '~/store/reducers/timetable'
import { getUserLastName } from '~/store/reducers/users'
import { htmlDecode } from '~/utils/htmlUtils'
import { useEventType } from '~/utils/useEventType'

import PatientMembershipIcon from '../../clients/patient/PatientMembershipIcon'
import { getHourAppointmentRange } from '../timetableUtils'
import CardAlert from './CardAlert'

const useStyles = makeStyles(
  (theme) => ({
    content: {
      position: 'relative',
    },
    contentUltraDense: {
      paddingTop: 0,
    },
    contentDense: {
      padding: theme.spacing(0.5, 1, 0),
    },
    cardText: {
      color: 'inherit',
    },
    cardTextTiny: {
      fontSize: '0.8rem',
    },
    disabledText: {
      color: theme.colors.disabledLabelText,
    },
    warningText: {
      color: theme.colors.important,
    },
    normalText: {
      color: theme.colors.title,
    },
    label: {
      padding: 0,
      wordBreak: 'break-word',
    },
    successLabel: {
      backgroundColor: theme.colors.successBackground,
      color: theme.colors.success,
    },
    warningMessage: {
      paddingTop: 1,
      paddingBottom: 1,
      paddingLeft: theme.spacing(0.75),
      paddingRight: theme.spacing(0.75),
      backgroundColor: theme.colors.important,
      borderRadius: 2,
      color: theme.colors.profileText,
    },
    membershipIcon: {
      position: 'absolute',
      right: 4,
      top: 4,
      fontSize: '1.7rem',
      zIndex: theme.utils.modifyZIndex(theme.zIndex.base, 'above'),
    },
  }),
  { name: 'ColumnCard' },
)

export interface ColumnCardProps {
  appointmentId: string
  contentStyle?: React.CSSProperties
  isDisabledState?: boolean
  isHalfWidth?: boolean
  isNormalState?: boolean
  isSuccessState?: boolean
  isWarningState?: boolean
  showAppointmentType?: boolean
  showHourRange?: boolean
  showSpace?: boolean
  warningMessage?: string
}

const ColumnCard = ({
  appointmentId,
  isDisabledState,
  isNormalState,
  isSuccessState,
  isWarningState,
  showHourRange,
  isHalfWidth,
  contentStyle,
  showAppointmentType,
  showSpace,
  warningMessage,
}: ColumnCardProps) => {
  const classes = useStyles()
  const appointment = useSelector(getTimetableEvent(appointmentId))
  const AppointmentTypes = useSelector(getAppointmentTypes)
  const EventState = useSelector(getEventState)
  const patient = useSelector(getPatient(appointment?.patient))
  const lastName = useSelector(getUserLastName(appointment?.client)) || ''
  const spaceName = useSelector(getSpaceName(appointment?.assignedSpace))

  const patientName = patient?.name || ''
  const AppointmentStates: AppointmentEventType['states'] = useEventType(
    EventTypeName.Appointment,
    'states',
  )
  const dischargedStatusId =
    Utils.findConstantIdByName('Discharged', AppointmentStates) || {}
  const isDischargedStatus = appointment?.stateId === dischargedStatusId

  const appointmentTypeStr =
    showAppointmentType && appointment?.typeId
      ? LanguageUtils.getConstantTranslatedName(
          appointment?.typeId,
          AppointmentTypes,
        )
      : ''

  const appointmentTypeNameStr = showAppointmentType
    ? LanguageUtils.getTranslatedFieldName(appointment?.businessAppointmentType)
    : appointmentTypeStr

  const hourRangeStr =
    showHourRange && appointment?.typeId
      ? getHourAppointmentRange(appointment)
      : ''
  const appointmentFullStr = LanguageUtils.getSentence(
    [appointmentTypeNameStr, hourRangeStr],
    SentenceFormatter.CAPITALIZE,
    ' | ',
  )
  const showAppointmentFullStr = Boolean(appointmentFullStr)

  const { alertsToRender } = useAlertType(appointment?.patient)
  const showAlertLabel = alertsToRender.length > 0

  return (
    <Grid container item direction="column" style={contentStyle} wrap="nowrap">
      {showAlertLabel && (
        <CardAlert
          alertColorId={patient?.alertColorId}
          alertTypes={alertsToRender}
        />
      )}
      <Grid
        container
        item
        className={classNames(classes.content, {
          [classes.contentDense]: showAlertLabel && !isHalfWidth,
          [classes.contentUltraDense]: showAlertLabel && isHalfWidth,
        })}
        direction="column"
        pb={0.25}
        pt={1}
        px={1}
        wrap="nowrap"
      >
        <PatientMembershipIcon
          hideIfNoMembership
          className={classes.membershipIcon}
          patientId={appointment?.patient}
        />
        <Grid
          item
          xs
          className={classNames({
            [classes.warningText]: isWarningState,
            [classes.disabledText]: isDisabledState,
            [classes.normalText]: isNormalState,
            [classes.successLabel]: isSuccessState,
          })}
        >
          <Dotdotdot clamp={1}>
            <Text strong className={classes.cardText} variant="subheading3">
              {`${patientName} ${isHalfWidth ? '' : lastName}`}
            </Text>
          </Dotdotdot>
          {isHalfWidth && (
            <Dotdotdot clamp={1}>
              <Text strong className={classes.cardText} variant="subheading3">
                {lastName}
              </Text>
            </Dotdotdot>
          )}
          {showAppointmentFullStr && (
            <Dotdotdot clamp={2}>
              <Text
                className={classNames(classes.cardText, {
                  [classes.cardTextTiny]: isHalfWidth,
                })}
                variant="body3"
              >
                {appointmentFullStr}
              </Text>
            </Dotdotdot>
          )}
          {showSpace && appointment?.assignedSpace && !isDischargedStatus && (
            <Dotdotdot clamp={1}>
              <Text
                className={classNames(classes.cardText, {
                  [classes.cardTextTiny]: isHalfWidth,
                })}
                variant="body3"
              >
                {spaceName}
              </Text>
            </Dotdotdot>
          )}
          {!isHalfWidth && Boolean(appointment?.notes) && (
            <Dotdotdot clamp={2}>
              <Text variant="body4">
                {htmlDecode(appointment?.notes || '')}
              </Text>
            </Dotdotdot>
          )}
        </Grid>
        <Grid container item alignItems="center" lineHeight={1} wrap="nowrap">
          <Dotdotdot clamp={1}>
            <StateLabel
              className={classes.label}
              disabled={isDisabledState}
              display="inline"
              normal={isNormalState}
              success={isSuccessState}
              variant="small"
              warning={isWarningState}
            >
              {LanguageUtils.getConstantTranslatedName(
                appointment?.stateId,
                EventState,
              )}
            </StateLabel>
          </Dotdotdot>
          {!isHalfWidth && isWarningState && warningMessage && (
            <Dotdotdot clamp={1}>
              <Text
                className={classes.warningMessage}
                display="inline"
                ml={0.5}
                variant="body3"
              >
                {warningMessage}
              </Text>
            </Dotdotdot>
          )}
        </Grid>
      </Grid>
    </Grid>
  )
}

export default ColumnCard
